import React, { useEffect, useState } from "react";
import api from "@api";
import { useAuth } from "../../../../context/AuthContext";
import "./Sponsors.css";
import { useParams } from "react-router-dom";

function Sponsors() {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [features, setFeatures] = useState([]);
  const { id } = useParams();
  const organizationId = id || user?.organization_id;
  const [bannerSettings, setBannerSettings] = useState({
    players: { rotationSpeed: 5, animation: "fade" },
    teams: { rotationSpeed: 5, animation: "fade" },
    schedule: { rotationSpeed: 5, animation: "fade" },
  });
  const [openSection, setOpenSection] = useState(null);

  // 🟢 Fetch banners + features
  useEffect(() => {
    if (!organizationId) return;

    const fetchData = async () => {
      try {
        const res = await api.get(
          `/api/organization-banner/organization/${organizationId}`
        );

        const banners = res.data.banners || [];

        // 🔥 Fetch stats for each banner
        const bannersWithStats = await Promise.all(
          banners.map(async (banner) => {
            try {
              const statsRes = await api.get(
                `/api/organization-banner/banner-click/${banner.id}/stats`
              );
              return { ...banner, stats: statsRes.data };
            } catch {
              return { ...banner, stats: { total: 0, last24: 0, last7: 0, last30: 0 } };
            }
          })
        );

        setBanners(bannersWithStats);

        const featureRes = await api.get(
          `/api/organization-features/${organizationId}`
        );
        setFeatures(featureRes.data.features || []);
        const placements = ["players", "teams", "schedule"];

        for (const p of placements) {
          const settingsRes = await api.get(
            `/api/organization-features/${organizationId}/banner-settings/${p}`
          );
          setBannerSettings(prev => ({
            ...prev,
            [p]: settingsRes.data
          }));
        }
      } catch (err) {
        console.error("Error loading banners:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  // 🟢 Upload directly into a placement
  const handleUploadToPlacement = async (e, placement) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("banner_image", file);
    formData.append("organization_id", organizationId);
    formData.append("placement", placement);

    try {
      setUploading(true);
      const res = await api.post("/api/organization-banner/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setBanners((prev) => [res.data.banner, ...prev]);
    } catch (err) {
      console.error("Banner upload error:", err);
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  // 🔴 Delete banner
  const handleDelete = async (bannerId) => {
    if (!window.confirm("Delete this banner?")) return;

    try {
      await api.delete(`/api/organization-banner/${bannerId}`);
      setBanners((prev) => prev.filter((b) => b.id !== bannerId));
    } catch (err) {
      console.error("Delete banner error:", err);
    }
  };

  // 🟡 Update locally
  const handlePlacementChange = (bannerId, newPlacement) => {
    setBanners((prev) =>
      prev.map((b) =>
        b.id === bannerId ? { ...b, placement: newPlacement } : b
      )
    );
  };

  // 🟢 Save to backend
  const savePlacement = async (bannerId, placement, link_url) => {
    if (!placement) {
      alert("Select a placement.");
      return;
    }

    try {
      await api.put(`/api/organization-banner/${bannerId}`, {
        placement,
        link_url,
      });
      alert("Banner updated!");
    } catch (err) {
      console.error("Update error:", err);
    }
  };
  const updateLocalSetting = (placement, key, value) => {
    setBannerSettings(prev => ({
      ...prev,
      [placement]: {
        ...prev[placement],
        [key]: value
      }
    }));
  };

  // 🟢 Helper to render each banner card
  const renderBannerCard = (b) => {
    const finalUrl =
      b.link_url?.startsWith("http") ? b.link_url : `https://${b.link_url}`;

    return (
      <div key={b.id} className="branding-banner-item">
        <img src={b.image_url} alt="Banner" className="branding-banner-img" />

        {editingBannerId !== b.id ? (
          <>
            <div className="branding-info">
              <p>
                <strong>Placement:</strong> {b.placement}
              </p>

              {b.link_url && (
                <p>
                  <strong>Link:</strong>{" "}
                  <a href={finalUrl} target="_blank" rel="noopener noreferrer">
                    {finalUrl}
                  </a>
                </p>
              )}
            </div>
            {b.stats && (
              <div className="branding-stats">
                <p>24h: {b.stats.last24}</p>
                <p>7d: {b.stats.last7}</p>
                <p>30d: {b.stats.last30}</p>
                <p>Total: {b.stats.total}</p>
              </div>
            )}
            <div className="branding-actions">
              <button onClick={() => setEditingBannerId(b.id)}>Edit</button>
              <button onClick={() => handleDelete(b.id)}>Delete</button>
            </div>
          </>
        ) : (
          <>
            <div className="branding-field">
              <label>Placement</label>
              <select
                value={b.placement || ""}
                onChange={(e) =>
                  handlePlacementChange(b.id, e.target.value)
                }
              >
                <option value="">-- Select Placement --</option>
                <option value="players">Players</option>
                <option value="teams">Teams</option>
                <option value="schedule">Schedule</option>
              </select>
            </div>

            <div className="branding-field">
              <label>Link URL</label>
              <input
                type="text"
                value={b.link_url || ""}
                onChange={(e) =>
                  setBanners((prev) =>
                    prev.map((banner) =>
                      banner.id === b.id
                        ? { ...banner, link_url: e.target.value.trim() }
                        : banner
                    )
                  )
                }
              />
            </div>

            <div className="branding-actions">
              <button
                onClick={() => {
                  const finalUrl =
                    b.link_url?.startsWith("http")
                      ? b.link_url
                      : `https://${b.link_url}`;
                  savePlacement(b.id, b.placement, finalUrl);
                  setEditingBannerId(null);
                }}
              >
                Save
              </button>

              <button onClick={() => setEditingBannerId(null)}>Cancel</button>
            </div>
          </>
        )}
      </div>
    );
  };

  // 🟢 Feature flags
  const allowPlayerBanner = features.some(
    (f) => f.feature_key === "banner_player" && f.enabled
  );
  const allowTeamBanner = features.some(
    (f) => f.feature_key === "banner_team" && f.enabled
  );
  const allowScheduleBanner = features.some(
    (f) => f.feature_key === "banner_schedule" && f.enabled
  );

  if (loading) return <p>Loading...</p>;

  return (
    <div className="branding-container">
      <h2 className="branding-title">Branding</h2>

      {/* ===================== PLAYERS PAGE ===================== */}
      {allowPlayerBanner && (
        <section
          className={`branding-section ${openSection === "players" ? "open" : ""
            }`}
        >
          <h3
            onClick={() =>
              setOpenSection(openSection === "players" ? null : "players")
            }
          >
            Players Page Banners
          </h3>

          <div className="branding-section-body">

            {/* ===== BANNERS FIRST ===== */}
            <div className="branding-preview-box">
              {banners
                .filter((b) => b.placement === "players")
                .map(renderBannerCard)}

              <label className="branding-upload-label">
                + Add Banner
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleUploadToPlacement(e, "players")
                  }
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {/* ===== SETTINGS BELOW ===== */}
            <div className="branding-settings-box">
              <h4>Slideshow Settings</h4>

              <label>Rotation Speed</label>
              <select
                value={bannerSettings.players.rotationSpeed}
                onChange={(e) =>
                  updateLocalSetting(
                    "players",
                    "rotationSpeed",
                    Number(e.target.value)
                  )
                }
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}s
                  </option>
                ))}
              </select>

              <label>Animation</label>
              <select
                value={bannerSettings.players.animation}
                onChange={(e) =>
                  updateLocalSetting(
                    "players",
                    "animation",
                    e.target.value
                  )
                }
              >
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
                <option value="slide-left">Slide Left</option>
              </select>

              <button
                onClick={async () => {
                  await api.put(
                    `/api/organization-features/${organizationId}/banner-settings/players`,
                    bannerSettings.players
                  );
                  alert("Player banner settings saved!");
                }}
              >
                Save Settings
              </button>
            </div>

          </div>
        </section>
      )}
      {/* ===================== TEAMS PAGE ===================== */}
      {allowTeamBanner && (
        <section
          className={`branding-section ${openSection === "teams" ? "open" : ""
            }`}
        >
          <h3
            onClick={() =>
              setOpenSection(openSection === "teams" ? null : "teams")
            }
          >
            Teams Page Banners
          </h3>

          <div className="branding-section-body">

            {/* ===== BANNERS FIRST ===== */}
            <div className="branding-preview-box">
              {banners
                .filter((b) => b.placement === "teams")
                .map(renderBannerCard)}

              <label className="branding-upload-label">
                + Add Banner
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleUploadToPlacement(e, "teams")
                  }
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {/* ===== SETTINGS BELOW ===== */}
            <div className="branding-settings-box">
              <h4>Slideshow Settings</h4>

              <label>Rotation Speed</label>
              <select
                value={bannerSettings.teams.rotationSpeed}
                onChange={(e) =>
                  updateLocalSetting(
                    "teams",
                    "rotationSpeed",
                    Number(e.target.value)
                  )
                }
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}s
                  </option>
                ))}
              </select>

              <label>Animation</label>
              <select
                value={bannerSettings.teams.animation}
                onChange={(e) =>
                  updateLocalSetting(
                    "teams",
                    "animation",
                    e.target.value
                  )
                }
              >
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
                <option value="slide-left">Slide Left</option>
              </select>

              <button
                onClick={async () => {
                  await api.put(
                    `/api/organization-features/${organizationId}/banner-settings/teams`,
                    bannerSettings.teams
                  );
                  alert("Team banner settings saved!");
                }}
              >
                Save Settings
              </button>
            </div>

          </div>
        </section>
      )}

      {/* ===================== SCHEDULE PAGE ===================== */}
      {allowScheduleBanner && (
        <section
          className={`branding-section ${openSection === "schedule" ? "open" : ""
            }`}
        >
          <h3
            onClick={() =>
              setOpenSection(
                openSection === "schedule" ? null : "schedule"
              )
            }
          >
            Schedule Page Banners
          </h3>

          <div className="branding-section-body">

            {/* ===== BANNERS FIRST ===== */}
            <div className="branding-preview-box">
              {banners
                .filter((b) => b.placement === "schedule")
                .map(renderBannerCard)}

              <label className="branding-upload-label">
                + Add Banner
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleUploadToPlacement(e, "schedule")
                  }
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {/* ===== SETTINGS BELOW ===== */}
            <div className="branding-settings-box">
              <h4>Slideshow Settings</h4>

              <label>Rotation Speed</label>
              <select
                value={bannerSettings.schedule.rotationSpeed}
                onChange={(e) =>
                  updateLocalSetting(
                    "schedule",
                    "rotationSpeed",
                    Number(e.target.value)
                  )
                }
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}s
                  </option>
                ))}
              </select>

              <label>Animation</label>
              <select
                value={bannerSettings.schedule.animation}
                onChange={(e) =>
                  updateLocalSetting(
                    "schedule",
                    "animation",
                    e.target.value
                  )
                }
              >
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
                <option value="slide-left">Slide Left</option>
              </select>

              <button
                onClick={async () => {
                  await api.put(
                    `/api/organization-features/${organizationId}/banner-settings/schedule`,
                    bannerSettings.schedule
                  );
                  alert("Schedule banner settings saved!");
                }}
              >
                Save Settings
              </button>
            </div>

          </div>
        </section>
      )}
    </div>
  );
}

export default Sponsors;